import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CDCNCompanyService } from './cdcn-company-service/cdcn-company.service';
import { CommonFunctions } from '../shared/utilities/common-functions';
import { environment } from 'src/environments/environment';
import { FeatureStateService } from '../shared/services/featureStateService';
import { ICompany } from './../core/models/company';
import { NotificationFunctionService } from '../core/services/notificationsapi/notificationfunctionapi.services';
import { ShellService } from '../core/services/shell.service';
import { StorageConstants } from '../shared/constants/storage-constants';
import { Store } from '@ngrx/store';
import { UnloadPersonsTypeSearchCriteria } from '../u2-person/person-state/person.actions';
import { PeopleFunctionService } from '../core/services/peopleapi/peoplefunctionapi.services';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'CDMS-Portal-U2-main-header',
    templateUrl: './main-header.component.html',
    styleUrls: ['./main-header.component.scss'],
    standalone: false
})
export class MainHeaderComponent implements OnInit, OnDestroy {

  public companyList: Array<ICompany> = [];
  public companyCode = '';
  public notReadNotifications = 0;
  public showNotifications = false;
  public sidebarCollapseClick = false;
  public personFromObjectId: any;
  public environment = '';
  public url: any;
  private readonly _destroying$ = new Subject<void>();

  constructor(
    private readonly cdcnCompanyService: CDCNCompanyService,
    public commonFunctions: CommonFunctions,
    private readonly featureState: FeatureStateService,
    private readonly NotificationService: NotificationFunctionService,
    private readonly peopleApiService: PeopleFunctionService,
    private readonly router: Router,
    private readonly shellService: ShellService,
    private readonly store: Store,
    private readonly toastr: ToastrService,
  ) {
    this.environment = environment.envName;
  }

  ngOnInit() {
    this.shellService.isShellDataLoaded$
    .pipe(
      takeUntil(this._destroying$)
    )
    .subscribe((loadData: boolean) => {
        if (loadData) {
          this.loadData();
          this.getUnreadCount();
        }
      }
    );
    this.shellService.fetchNotifications$
    .pipe(
      takeUntil(this._destroying$)
    )
    .subscribe((fetchNotifications: boolean) => {
      if (fetchNotifications) {
        this.getUnreadCount();
      }
    });
  }

  sidebarClick() {
    this.removeAllSubMenu();
    this.sidebarCollapseClick = !this.sidebarCollapseClick;
    this.updateSideBarComputeStyle();
    this.shellService.setSideNavCollapse(this.sidebarCollapseClick);
  }

  /**
   * Handles computed styles not getting updated issue.
   * NOTE:
   * - This is a workaround for a known issue with CSS class toggling not reflecting immediately in some scenarios.
   * - If the issue still persists, Angular-driven approach should be considered.
   */
  updateSideBarComputeStyle() {
    const iconCollapse = document.querySelector('.sidebar-collapse i.collapse-left');
    const iconExpand = document.querySelector('.sidebar-collapse i.expand-list');

    if (this.sidebarCollapseClick) {
      if (iconCollapse) {
        (iconCollapse as HTMLElement).style.display = 'none';
      }
      if (iconExpand) {
        (iconExpand as HTMLElement).style.display = 'inline-block';
      }
    } else {
      if (iconCollapse) {
        (iconCollapse as HTMLElement).style.display = 'inline-block';
      }
      if (iconExpand) {
        (iconExpand as HTMLElement).style.display = 'none';
      }
    }
  }

  companyChange(eventData: string) {
    if (this.companyCode === eventData) {
      return;
    }
    const company = this.companyList.find(x => x.CompanyName === eventData);
    this.companyCode = company.CompanyName;
    sessionStorage.setItem(StorageConstants.CompanyCode, company.CompanyName);
    sessionStorage.setItem(StorageConstants.CompanyId, company.CompanyId.toString());
    this.cdcnCompanyService.clearControllers();
    this.cdcnCompanyService.clearSessionStorage();
    this.store.dispatch(new UnloadPersonsTypeSearchCriteria());
    if (this.router.url === ('/' + this.shellService.navigationData.firstmenuUrl)) {
      window.location.reload();
    }
    else {
      this.shellService.setCompanyChange(true);
    }
  }

  goToNotificationsmessages() {
    this.router.navigateByUrl('Maintenance/Notifications/NotificationMessages/view');
  }

  debugChangeEnvironmentStyle(event) {
    if (!event.shiftKey || environment.envName === 'prod') { return; }
    const names = ['dev', 'qa', 'int', 'perf', 'alpha', 'migration', 'uat', 'preprod', 'prod'];
    let index = names.indexOf(this.environment) + 1;
    if (!names[index]) { index = 0; }
    this.environment = names[index];
  }

  logout() {
    this.shellService.setLogoutObservable(true);
  }

  ngOnDestroy(): void {
    this._destroying$.next(null);
    this._destroying$.complete();
  }

  get userName() {
    return localStorage.getItem(StorageConstants.UserName) ?? '';
  }

  private loadData() {
    this.companyCode = sessionStorage.getItem(StorageConstants.CompanyCode);
    this.companyList = this.updateCompaniesList(
      this.shellService.navigationData.companyList.map(i => (
        {
          CompanyId : i.companyId,
          CompanyName: i.companyName
        }
      )
    ));
  }

  private updateCompaniesList(dt) {
    const companyList = <Array<ICompany>>dt;
    companyList.sort((a, b) => {
      if (a.CompanyName > b.CompanyName) {
        return 1;
      } else if (b.CompanyName > a.CompanyName) {
        return -1;
      } else {
        return 0;
      }
    });

    return companyList;
  }

  private removeAllSubMenu() {
    const openSubmenu = Array.from(document.querySelectorAll('.show.sub-menu'));
    openSubmenu.forEach(element => {
      element.classList.remove('show');
    });
    this.openMenuArrow();
  }

  private openMenuArrow() {
    const arrow = Array.from(document.querySelectorAll('b'));
    if (arrow) {
      arrow.forEach(element => {
        element.classList.remove('down');
        element.classList.add('right');
      });
    }
  }

  private getUnreadCount() {
    this.showNotifications = this.featureState.isNotificationEnabled();
    this.peopleApiService.getPersonIdByUserObjectId(
      this.shellService.companyAndScreenPermissionsData.screenPermissions.objectId
    ).pipe(
      takeUntil(this._destroying$)
    ).subscribe({
      next: (personId: any) => {
        sessionStorage.setItem(StorageConstants.PersonListFromObjectId, JSON.stringify(personId));
        if(personId.length > 0) {
          this.personFromObjectId = personId[0].personId;
        }
        if (this.showNotifications && this.personFromObjectId) {
          this.NotificationService.getNotificationUnReadCnt(this.personFromObjectId).pipe(
            takeUntil(this._destroying$)
          ).subscribe({
            next: (result) => {
              if (result) {
                this.notReadNotifications = result;
              } else {
                this.notReadNotifications = 0;
              }
            },
            error: (error) => {
              this.commonFunctions.handleErrorResponse(error, this.toastr);
            }
          });
        }
      },
      error: (error) => {
        this.commonFunctions.handleErrorResponse(error, this.toastr);
      }
    });
  }
}
